import { captureMessage } from "@/error/sentry";

import TextMeasurer, { wordDownsize } from "./TextMeasurer";
import type { DisplayFontData, EditFontData } from "./fontSizeCache";
import { cacheDisplayFontData, cacheEditFontData } from "./fontSizeCache";
import { stringsFromLines } from "./lineBreaker";
import { replaceLinks, toHtmlWithLinks } from "./linkParser";

const maxSteps = 7;

interface Options {
  isDependency?: boolean;
  nonStandardCardSize?: boolean; // For rendering cards off-board
}

export function optimalEditFontSize(
  el: HTMLElement,
  text: string,
  font: string,
  options: Options = {},
): EditFontData {
  const key = cacheKey(el, text, font, options);
  return cacheEditFontData(key, () => calcEditFontSize(el, text));
}

export function optimalDisplayFontSize(
  el: HTMLElement,
  text: string,
  font: string,
  options: Options = {},
): DisplayFontData {
  const key = cacheKey(el, text, font, options);
  return cacheDisplayFontData(
    key,
    () => calcEditFontSize(el, text),
    (data) => calcDisplayFontSize(data, el, text),
  );
}

function cacheKey(el: HTMLElement, text: string, font: string, options: Options) {
  let width = 224;
  let height = options.isDependency ? 132 : 160;

  // When rendering cards off-board (eg. plan readout), use the el's actual size
  // instead of the standard card size (performance is less of a concern, because
  // we're only rendering 1 card at a time)
  if (options.nonStandardCardSize) {
    width = el.clientWidth;
    height = el.clientHeight;
    if (width <= 0 || height <= 0) {
      captureMessage("No size to calc fontData");
    }
  }

  const ratio = Math.round((100 * width) / height);
  return `${ratio}/${font}/${text}`;
}

function calcEditFontSize(el: HTMLElement, text: string) {
  const measurer = TextMeasurer.of(el)!;

  const maxFontSize = measurer.maxFontSize();
  const wordSize = wordDownsize(measurer.maxFontSizeForText(), text);
  let max = maxFontSize * wordSize;

  let min = max / 20;
  for (let i = 0; i < maxSteps; i++) {
    const size = (min + max) / 2;
    const measure = measurer.measureLines(size, text);
    if (measure.height <= (el.clientHeight || el.offsetHeight)) {
      min = size;
    } else {
      max = size;
    }
  }
  min = Math.round(min * 1000) / 1000;

  measurer.setFontSize(min);
  const minMeasure = measurer.measureLines(min, text);
  return { size: min, lines: minMeasure.lines };
}

function calcDisplayFontSize(data: EditFontData, el: HTMLElement, text: string) {
  const { text: linkText, links } = replaceLinks(text);
  const display = links.length > 0 ? calcEditFontSize(el, linkText) : undefined;
  const lines = display?.lines || data.lines;
  return {
    ...data,
    display,
    htmlLines: toHtmlWithLinks(lines, linkText, links),
    textLines: stringsFromLines(lines, linkText),
  };
}
